/**
 * @fileoverview 44px (or any size) neighborhood footprint SVG glyph (WEB-648).
 *
 * Pure presentational — callers supply a normalised unit-viewBox path `d`
 * from `useNeighborhoodFootprints` / `boundaryToFootprintPath`. Same path
 * renders at hoodmark and hero scale without re-projection.
 *
 * Lives in `atoms/` because it is a single-purpose SVG primitive that composes
 * nothing — the same shape as `OringMark`. Molecules are composites of atoms.
 */

import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';

export interface NeighborhoodFootprintMarkProps {
  /** Normalised SVG path `d` in a unit viewBox. */
  path: string;
  /** Rendered width/height in CSS pixels. Defaults to the 44px hoodmark. */
  size?: number;
  /** Optional accessible name; decorative when omitted. */
  label?: string;
  /** Fill color (defaults to currentColor). */
  fill?: string;
  className?: string;
  'data-testid'?: string;
}

export function NeighborhoodFootprintMark({
  path,
  size = 44,
  label,
  fill = 'currentColor',
  className,
  'data-testid': testId = 'neighborhood-footprint-mark',
}: NeighborhoodFootprintMarkProps): React.ReactElement {
  const decorative = !label;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 1 1"
      className={cn('inline-block shrink-0', className)}
      role={decorative ? 'presentation' : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : label}
      data-testid={testId}
    >
      {/*
       * `evenodd` (not the SVG default `nonzero`) so interior rings render as
       * holes. `boundaryToFootprintPath` emits one subpath per GeoJSON ring
       * without normalising winding, and the boundary source does not
       * guarantee RFC 7946's CCW-exterior / CW-interior convention — under
       * `nonzero`, two same-winding rings fill the hole in solid.
       */}
      <path d={path} fill={fill} fillRule="evenodd" />
    </svg>
  );
}
